import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';

const Header: React.FC = () => {
  const { isLoggedIn, logout, login } = useAuth();
  const { showLoginModal, setShowLoginModal } = useModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'industries' | 'products' | null>(null);
  const [isHeroInView, setIsHeroInView] = useState(true);
  
  // Timeout refs for hover delays
  const industriesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const productsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const industries = [
    {
      id: "banking",
      name: "Banking",
      punchyLine: "Verify faster. Stay compliant. Stop fraud.",
      description: "Trust Every Account You Onboard - Spot digital fraud before it reaches your core systems.",
      icon: (
        <img src="/use-cases-icons/banking.png" alt="Banking" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "All-in-One Fraud View: Banks need one system to track fraud across apps, websites, and branches together.",
        "Strong Compliance: Real-time checks for KYC, AML, and FATF rules help banks stay audit-ready and penalty-free.",
        "Faster Operations: Automated fraud screening reduces manual checks and helps onboard genuine customers quickly.",
        "Connected Insights: Combining data from behaviour, device, and transactions gives a clearer picture of risky accounts."
      ],
      fraudLandscape: "Banks battle constant waves of mule accounts, laundering rings, and synthetic IDs. Each threat jeopardizes trust and compliance.",
      howBargadHelps: "Bargad.ai detects mule behaviour in milliseconds - enabling frictionless verification, smarter alerting, and proactive fraud prevention before the funnel is breached.",
      keyCapabilities: [
        "AML / KYC automation without friction",
        "Smart prioritisation of high-risk alerts",
        "Reduced false positives through contextual signals",
        "Unified fraud defence across channels"
      ]
    },
    {
      id: "nbfc",
      name: "Lending",
      punchyLine: "Stay ahead of evolving credit risk.",
      description: "See Risk from Every Angle - Lend with confidence and detect high-risk profiles before they enter the funnel.",
      icon: (
        <img src="/use-cases-icons/lending.png" alt="Lending" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "Quick Credit Decisions: NBFCs need fast and accurate verification tools to approve loans smoothly without long delays.",
        "Smart Risk Updates: Risk profiles must update automatically when a customer's behavior or transactions change.",
        "Easy Compliance: Automated systems are needed to stay compliant with RBI and FIU-IND guidelines effortlessly.",
        "Scalable Protection: Fraud detection tools should grow with the business and work across all loan types - personal, vehicle, or business loans."
      ],
      fraudLandscape: "With rising synthetic identities and mule networks, NBFCs face hidden threats that erode financial integrity.",
      howBargadHelps: "Bargad.ai fuses behavioural analytics and link intelligence to uncover invisible risk patterns, empowering lenders to make faster, safer credit decisions.",
      keyCapabilities: [
        "Real-time mule and network detection",
        "Adaptive AI-driven risk scoring",
        "Instant compliance with minimal friction",
        "Seamless API integration"
      ]
    },
    {
      id: "insurance",
      name: "Insurance",
      punchyLine: "Secure every policy lifecycle.",
      description: "Expose Fraud Before It Takes Form - Block fake claims and policy misuse with early fraud signals.",
      icon: (
        <img src="/use-cases-icons/insurance.png" alt="Insurance" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "Early Fraud Alerts: Insurers need to spot fake or suspicious policy applications before they turn into false claims.",
        "Identity Match Checks: It's important to confirm if the same person is applying multiple times using different details.",
        "Shared Fraud Intelligence: Insurance companies benefit from connecting with other insurers to identify repeat offenders.",
        "Smooth Customer Experience: Fraud detection should run quietly in the background without affecting genuine policyholders."
      ],
      fraudLandscape: "From ghost brokers to cloned policies, insurance fraud starts at onboarding and matures into costly claims.",
      howBargadHelps: "Bargad.ai analyses behavioural, device, and network signals to reveal intent early - ensuring genuine customers pass through while fraudsters are filtered out.",
      keyCapabilities: [
        "Detect fake or stolen identities instantly",
        "Identify collusive or repeated fraud rings",
        "Pre-onboarding risk scoring",
        "Lower claim losses and stronger trust"
      ]
    },
    {
      id: "gaming",
      name: "Gaming",
      punchyLine: "Keep your arena fair and fraud-free.",
      description: "Where Fair Play Meets Smart Defence - Catch bots, mule rings, and exploiters before they play.",
      icon: (
        <img src="/use-cases-icons/gaming.png" alt="Gaming" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "Real-Time Player Checks: Platforms must verify players instantly to block fake accounts and bots.",
        "Safe In-Game Economy: Protection is needed for digital money, items, and bonuses from misuse or fraud.",
        "Fair Competition: Tools should help maintain transparency and fairness across all multiplayer environments.",
        "Adaptive Protection: The system should keep learning and adapting as new gaming fraud tricks emerge."
      ],
      fraudLandscape: "Gaming platforms face constant abuse - bots, fake profiles, bonus manipulation, and mule networks that distort fair competition and revenue.",
      howBargadHelps: "Bargad.ai blends behavioural and device intelligence to monitor gameplay, purchases, and interactions in real time - keeping fraud checks invisible while preserving the player experience.",
      keyCapabilities: [
        "Detect fake players, bots, and mule networks before entry",
        "Protect in-game currencies and promo programs",
        "Continuous anomaly monitoring across gameplay and payments",
        "Maintain trust through fair, transparent play"
      ]
    },
    {
      id: "matrimony",
      name: "Matrimony",
      punchyLine: "Fewer scams. Safer conversations. Real connections.",
      description: "Protect Love. Preserve Trust. - Keep your platform authentic by screening fake and misleading profiles.",
      icon: (
        <img src="/use-cases-icons/matrimony.png" alt="Matrimony" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "Real Identity Checks: Platforms need tools to confirm if profiles, photos, and details are genuine.",
        "Intent Verification: Analysing chat and activity patterns helps detect scammers or fake interests early.",
        "Safety by Design: Early detection of harassment, emotional manipulation, or money scams builds user trust.",
        "Privacy-First Approach: Users should stay protected while their data remains completely confidential."
      ],
      fraudLandscape: "Romance scams, catfishing, and synthetic identities damage emotions and reputations long before discovery.",
      howBargadHelps: "Bargad.ai merges identity, device, social, and behavioural intelligence to verify intent, unmask deception, and secure genuine connections - all while protecting privacy.",
      keyCapabilities: [
        "Unmask fake profiles via image, metadata, and reuse analysis",
        "Verify intent through behavioural and messaging patterns",
        "Expose hidden networks linking scam rings and ghost brokers",
        "Prioritise risky accounts for faster moderation",
        "Enforce safety without exposing user data"
      ]
    },
    {
      id: "ecommerce",
      name: "E-Commerce",
      punchyLine: "Higher conversion. Lower losses. Trusted marketplaces.",
      description: "Protect Revenue. Preserve Experience. - Stop fake users, return abuse, and coupon fraud at signup.",
      icon: (
        <img src="/use-cases-icons/ecommerce.png" alt="E-Commerce" className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
      ),
      industryRequirements: [
        "Genuine Buyer Verification: Platforms must confirm customer identities to stop fake signups and return abuse.",
        "Trusted Seller Network: Regular checks help ensure that only verified and authentic sellers stay on the platform.",
        "Safe Transactions: Real-time fraud screening is needed during checkout to catch unusual payment or device behaviour.",
        "Review Integrity: Platforms must stop fake reviews and maintain trust in the shopping experience."
      ],
      fraudLandscape: "E-commerce platforms lose margin to bots, promo abuse, fake listings, and refund manipulation - threatening both profit and trust.",
      howBargadHelps: "Bargad.ai unifies behavioural, image, and marketplace intelligence to secure checkout, vet sellers, and ensure authentic interactions without friction.",
      keyCapabilities: [
        "Block scalpers, automated checkout, and inventory hoarding",
        "Detect coupon stacking and coordinated offer exploitation",
        "Validate merchant identities and flag counterfeit listings",
        "Catch refund/return scams through device-location correlation",
        "Preserve review integrity via cross-account linkage and image matching"
      ]
    }
  ];

  const products = [
    { id: "digital-footprints", title: "Digital Footprints", subtitle: "Email, Phone, Social Media, Data Leaks", image: "/nine-box-model/digital footprint.png" },
    { id: "cognitive-behaviour", title: "Cognitive Behaviour", subtitle: "Behavioral analysis", image: "/nine-box-model/cognitive behaviour.png" },
    { id: "device-forensic", title: "Device Forensic", subtitle: "Hardware fingerprinting", image: "/nine-box-model/device forensic.png" },
    { id: "geolocation", title: "Geolocation", subtitle: "Location intelligence", image: "/nine-box-model/geo location.png" },
    { id: "network-quality", title: "Network Quality", subtitle: "IP address intelligence", image: "/nine-box-model/network connectivity.png" },
    { id: "mobile-to-sources", title: "Mobile to sources", subtitle: "Mobile to Address, Mobile to UPI, Mobile to LPG", image: "/nine-box-model/mobile to sources.png" },
    { id: "litigation-lookup", title: "Litigation Lookup", subtitle: "Criminal Check", image: "/nine-box-model/litigation lookup.png" },
    { id: "document-forged", title: "Document Forged", subtitle: "All docs", image: "/nine-box-model/doc forged.png" },
    { id: "transaction-patterns", title: "Transaction Patterns", subtitle: "Neural networks", image: "/nine-box-model/transaction patters.png" },
  ];

  const handleIndustriesDropdownClose = () => {
    setShowIndustriesDropdown(false);
  };

  // Hover handlers with timeout management
  const handleIndustriesMouseEnter = useCallback(() => {
    if (industriesTimeoutRef.current) {
      clearTimeout(industriesTimeoutRef.current);
      industriesTimeoutRef.current = null;
    }
    if (productsTimeoutRef.current) {
      clearTimeout(productsTimeoutRef.current);
      productsTimeoutRef.current = null;
    }
    setActiveDropdown('industries');
    setShowIndustriesDropdown(true);
    setShowProductsDropdown(false);
    
    // Preload all industry images when dropdown opens for faster navigation
    const imageUrls: Record<string, string> = {
      'banking': '/industry-hero-icons/banking fraud.png',
      'nbfc': '/industry-hero-icons/lending fraud.png',
      'insurance': '/industry-hero-icons/insurance fraud.png',
      'gaming': '/industry-hero-icons/game fraud.png',
      'matrimony': '/industry-hero-icons/matrimony fraud.png',
      'ecommerce': '/industry-hero-icons/ecommerce fraud.png',
    };
    
    const howBargadHelpsUrls: Record<string, string> = {
      'banking': '/how_bargad_helps/banking_hbh.png',
      'nbfc': '/how_bargad_helps/lending_hbh.png',
      'insurance': '/how_bargad_helps/insurance_hbh.png',
      'gaming': '/how_bargad_helps/ggaming_hbh.png',
      'matrimony': '/how_bargad_helps/matrimony_hbh.png',
      'ecommerce': '/how_bargad_helps/ecommerce_hbh.png',
    };
    
    // Preload all images with prefetch (lower priority, but starts early)
    Object.values(imageUrls).forEach(url => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
    
    Object.values(howBargadHelpsUrls).forEach(url => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  }, []);

  const handleIndustriesMouseLeave = useCallback(() => {
    industriesTimeoutRef.current = setTimeout(() => {
      setShowIndustriesDropdown(false);
      setActiveDropdown(null);
    }, 150);
  }, []);

  const handleProductsMouseEnter = useCallback(() => {
    if (productsTimeoutRef.current) {
      clearTimeout(productsTimeoutRef.current);
      productsTimeoutRef.current = null;
    }
    if (industriesTimeoutRef.current) {
      clearTimeout(industriesTimeoutRef.current);
      industriesTimeoutRef.current = null;
    }
    setActiveDropdown('products');
    setShowProductsDropdown(true);
    setShowIndustriesDropdown(false);
  }, []);

  const handleProductsMouseLeave = useCallback(() => {
    productsTimeoutRef.current = setTimeout(() => {
      setShowProductsDropdown(false);
      setActiveDropdown(null);
    }, 150);
  }, []);

  const handleSharedDropdownMouseEnter = useCallback(() => {
    // Clear any pending timeouts when hovering over the shared container
    if (industriesTimeoutRef.current) {
      clearTimeout(industriesTimeoutRef.current);
      industriesTimeoutRef.current = null;
    }
    if (productsTimeoutRef.current) {
      clearTimeout(productsTimeoutRef.current);
      productsTimeoutRef.current = null;
    }
  }, []);

  const handleSharedDropdownMouseLeave = useCallback(() => {
    // Use a longer timeout for the shared container to prevent flickering
    const timeoutId = setTimeout(() => {
      setShowIndustriesDropdown(false);
      setShowProductsDropdown(false);
      setActiveDropdown(null);
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Handle header background based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      setIsHeroInView(scrollY < 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (industriesTimeoutRef.current) {
        clearTimeout(industriesTimeoutRef.current);
      }
      if (productsTimeoutRef.current) {
        clearTimeout(productsTimeoutRef.current);
      }
    };
  }, []);

  // Preload login page image for faster loading
  useEffect(() => {
    const img = new Image();
    img.src = '/login-page-icons/developer portal.png';
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    if (loginData.email && loginData.password) {
      const success = await login(loginData.email, loginData.password);
      if (success) {
        setShowLoginModal(false);
        setLoginData({ email: '', password: '' });
      } else {
        setLoginError('Invalid credentials');
      }
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out ${
        isMenuOpen 
          ? 'bg-black/98 backdrop-blur-lg' 
          : isHeroInView 
            ? 'bg-transparent' 
            : 'bg-black/95 backdrop-blur-md'
      }`}>
        {/* Contact Banner */}
        <div className="bg-[#26ab4d] text-[#ffffff] py-1.5 lg:py-1">
          <div className="container mx-auto px-[1.5rem]">
            <div className="flex items-center justify-end">
              {/* Right Side - Social Media Icons */}
              <div className="flex items-center space-x-1 lg:space-x-2">
                {/* Twitter */}
                <div className="w-6 h-6 lg:w-7 lg:h-7 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-[#ffffff]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                
                {/* LinkedIn */}
                <div className="w-6 h-6 lg:w-7 lg:h-7 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-[#ffffff]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 c0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </div>
                
                {/* YouTube */}
                <div className="w-6 h-6 lg:w-7 lg:h-7 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-[#ffffff]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      {/* Main Navigation Bar */}
      <div className="mt-6 mb-4 lg:mt-0 lg:mb-0">
        <div className="container mx-auto px-[1.5rem] pt-4 pb-4 lg:pt-[1.5rem] lg:pb-[1.5rem]">
          <div className="flex items-center justify-between relative">
            {/* Logo Section */}
            <div className="flex items-center flex-shrink-0 z-10">
              <Link 
                to="/" 
                className="flex items-center group"
                aria-label="Bargad.ai Home"
              >
                    <img 
                      src="/new-logo.png" 
                      alt="Bargad.ai Logo" 
                      className="h-16 lg:h-20 w-auto object-contain"
                      loading="eager"
                      decoding="sync"
                    />
              </Link>
            </div>

            {/* Center Navigation */}
            <nav className="hidden lg:flex items-center justify-center flex-1 space-x-20 relative">
              {/* Use cases Dropdown */}
              <div className="relative group">
                <button 
                        className="text-[#ffffff] text-lg font-bold tracking-wide hover:text-[#1e8a3f] transition-colors whitespace-nowrap"
                  onMouseEnter={handleIndustriesMouseEnter}
                  onMouseLeave={handleIndustriesMouseLeave}
                >
                Use cases
                </button>
              </div>

              {/* Products Dropdown */}
              <div className="relative group">
                <button 
                        className="text-[#ffffff] text-lg font-bold tracking-wide hover:text-[#1e8a3f] transition-colors"
                  onMouseEnter={handleProductsMouseEnter}
                  onMouseLeave={handleProductsMouseLeave}
                >
                  Products
                </button>
              </div>
              
              {/* Shared Dropdown Container */}
              {(showIndustriesDropdown || showProductsDropdown) && (
                <div 
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-[600px] bg-black/90 border border-[#26ab4d]/30 rounded-lg shadow-lg z-50"
                  onMouseEnter={handleSharedDropdownMouseEnter}
                  onMouseLeave={handleSharedDropdownMouseLeave}
                >
                  <div className="p-4">
                    {/* Use cases Content */}
                    {activeDropdown === 'industries' && (
                      <div className="grid grid-cols-3 gap-3">
                        {industries.map((industry) => {
                          // Preload images on hover and click for faster page load
                          const imageUrls: Record<string, string> = {
                            'banking': '/industry-hero-icons/banking fraud.png',
                            'nbfc': '/industry-hero-icons/lending fraud.png',
                            'insurance': '/industry-hero-icons/insurance fraud.png',
                            'gaming': '/industry-hero-icons/game fraud.png',
                            'matrimony': '/industry-hero-icons/matrimony fraud.png',
                            'ecommerce': '/industry-hero-icons/ecommerce fraud.png',
                          };
                          
                          const howBargadHelpsUrls: Record<string, string> = {
                            'banking': '/how_bargad_helps/banking_hbh.png',
                            'nbfc': '/how_bargad_helps/lending_hbh.png',
                            'insurance': '/how_bargad_helps/insurance_hbh.png',
                            'gaming': '/how_bargad_helps/ggaming_hbh.png',
                            'matrimony': '/how_bargad_helps/matrimony_hbh.png',
                            'ecommerce': '/how_bargad_helps/ecommerce_hbh.png',
                          };
                          
                          const preloadIndustryImages = (industryId: string) => {
                            const heroImageUrl = imageUrls[industryId];
                            const hbhImageUrl = howBargadHelpsUrls[industryId];
                            
                            if (heroImageUrl) {
                              // Preload with high priority
                              const link = document.createElement('link');
                              link.rel = 'preload';
                              link.as = 'image';
                              link.href = heroImageUrl;
                              link.setAttribute('fetchpriority', 'high');
                              document.head.appendChild(link);
                              
                              // Also use Image constructor for immediate fetch
                              const img = new Image();
                              img.src = heroImageUrl;
                            }
                            
                            if (hbhImageUrl) {
                              const img = new Image();
                              img.src = hbhImageUrl;
                            }
                          };
                          
                          return (
                          <Link
                            key={industry.id}
                            to={`/use-cases/${industry.id}`}
                            className="group p-3 rounded-lg hover:bg-[#26ab4d]/10 transition-all duration-200 cursor-pointer border border-transparent hover:border-[#26ab4d]/20"
                            onClick={() => {
                              // Preload before navigation
                              preloadIndustryImages(industry.id);
                              handleIndustriesDropdownClose();
                            }}
                            onMouseEnter={() => preloadIndustryImages(industry.id)}
                          >
                            {/* Industry Icon */}
                            <div className="flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200">
                              {industry.icon}
                            </div>
                            
                            {/* Industry Title */}
                            <h4 className="text-[#ffffff] text-sm font-bold text-center group-hover:text-[#26ab4d] transition-colors">
                              {industry.name}
                            </h4>
                          </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Products Content */}
                    {activeDropdown === 'products' && (
                      <div className="grid grid-cols-3 gap-3">
                        {products.map((product) => (
                          <Link
                            key={product.id}
                            to={`/products/${product.id}`}
                            className="group block p-3 rounded-lg hover:bg-[#1e8a3f]/10 transition-all duration-200 cursor-pointer border border-transparent hover:border-[#1e8a3f]/20"
                            onClick={() => setShowProductsDropdown(false)}
                          >
                            {/* Product Image */}
                            <div className="flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200">
                              <img src={product.image} alt={product.title} className="w-10 h-10 object-contain max-w-10 max-h-10" loading="lazy" decoding="async" width="40" height="40" style={{ maxWidth: '40px', maxHeight: '40px' }} />
                            </div>
                            
                            {/* Product Title */}
                            <h4 className="text-[#ffffff] text-sm font-bold text-center group-hover:text-[#1e8a3f] transition-colors mb-2">
                              {product.title}
                            </h4>
                            
                            {/* Sub Products on hover */}
                            <p className="text-[#ffffff] text-[10px] text-center mt-2 leading-tight opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              {product.subtitle}
                            </p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Link to="/about" className="text-[#ffffff] text-lg font-bold tracking-wide hover:text-[#1e8a3f] transition-colors">
                Knowledge
              </Link>
            </nav>

            {/* Right Side - Action Buttons */}
            <div className="flex items-center justify-end flex-shrink-0 gap-1.5 lg:gap-4">
              {isLoggedIn ? (
                <>
                  <button 
                    onClick={logout}
                    className="bg-[#26ab4d] text-[#ffffff] px-3 lg:px-8 py-1.5 lg:py-4 rounded-full text-xs lg:text-lg font-bold hover:bg-[#1e8a3f] transition-colors duration-300 shadow-xl inline-flex items-center justify-center min-w-[70px] lg:min-w-[170px] whitespace-nowrap"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <div className="relative group hidden lg:block flex items-center">
                    <div className="relative flex flex-col items-center">
                      {/* Fixed Text - positioned absolutely above */}
                      <div className="absolute -top-4 lg:-top-5 text-[9px] lg:text-[10px] text-[#ffffff] whitespace-nowrap text-center w-full">
                        &lt;/&gt; API developer portal
                      </div>
                      <Link to="/ask-access" className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-3 lg:px-8 py-1.5 lg:py-4 rounded-full text-xs lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center text-center min-w-[70px] lg:min-w-[170px] whitespace-nowrap">
                        Ask for access
                      </Link>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowLoginModal(true)}
                    className="text-[#ffffff] text-xs lg:text-lg font-semibold tracking-wide hover:text-[#26ab4d] transition-colors whitespace-nowrap hidden lg:block"
                  >
                    Login
                  </button>
                </>
              )}
              
              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="lg:hidden flex items-center justify-center p-1 text-[#26ab4d] hover:text-[#1e8a3f] focus:outline-none touch-manipulation active:opacity-70"
                aria-label="Toggle menu"
              >
                <div className="w-5 h-5 flex flex-col justify-center">
                  <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </div>
              </button>
              </div>
            </div>
        </div>

        {/* Mobile Menu - Integrated into Header */}
        <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMenuOpen 
            ? 'max-h-[2000px] opacity-100 translate-y-0' 
            : 'max-h-0 opacity-0 -translate-y-4'
        }`}>
          <div className={`bg-gradient-to-b from-black via-black to-black/98 backdrop-blur-lg shadow-2xl shadow-[#26ab4d]/10 relative pt-6 ${isMenuOpen ? 'overflow-y-auto max-h-[calc(100vh-150px)]' : ''}`}>
            <nav className="px-[1.5rem] py-5 lg:py-6">
              <div className="flex flex-col space-y-6">
                {/* Use cases Mobile Dropdown */}
                <div className="border-b border-[#26ab4d]/20 pb-5">
                  <button 
                    className="text-[#ffffff] text-base font-bold tracking-wide transition-all duration-300 flex items-center justify-between w-full py-3 px-2 rounded-lg hover:bg-[#26ab4d]/10 hover:text-[#26ab4d] group active:bg-[#26ab4d]/15"
                    onClick={() => setShowIndustriesDropdown(!showIndustriesDropdown)}
                  >
                    <span className="whitespace-nowrap flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#26ab4d] opacity-60 group-hover:opacity-100 transition-opacity"></span>
                      Use cases
                    </span>
                    <svg className={`w-5 h-5 transition-all duration-300 text-[#26ab4d] group-hover:text-[#26ab4d] ${showIndustriesDropdown ? 'rotate-180 opacity-100' : 'opacity-70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showIndustriesDropdown && (
                    <div className="mt-5">
                      <div className="grid grid-cols-2 gap-3">
                        {industries.map((industry) => (
                          <Link
                            key={industry.id}
                            to={`/use-cases/${industry.id}`}
                            className="group relative p-4 rounded-2xl bg-gradient-to-br from-[#26ab4d]/8 via-[#26ab4d]/5 to-transparent hover:from-[#26ab4d]/20 hover:via-[#26ab4d]/12 hover:to-[#26ab4d]/5 transition-all duration-300 cursor-pointer border border-[#26ab4d]/20 hover:border-[#26ab4d]/50 hover:shadow-xl hover:shadow-[#26ab4d]/20 hover:-translate-y-0.5 active:translate-y-0"
                            onClick={() => {
                              handleIndustriesDropdownClose();
                              setIsMenuOpen(false);
                            }}
                          >
                            {/* Glow effect on hover */}
                            <div className="absolute inset-0 rounded-2xl bg-[#26ab4d]/0 group-hover:bg-[#26ab4d]/5 transition-all duration-300 blur-xl"></div>
                            
                            {/* Industry Icon */}
                            <div className="relative flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                              {industry.icon}
                            </div>
                            
                            {/* Industry Title */}
                            <h4 className="relative text-[#ffffff] text-xs font-bold text-center group-hover:text-[#26ab4d] transition-colors duration-300 leading-tight">
                              {industry.name}
                            </h4>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Products Mobile Dropdown */}
                <div className="border-b border-[#26ab4d]/20 pb-5">
                  <button 
                    className="text-[#ffffff] text-base font-bold tracking-wide transition-all duration-300 flex items-center justify-between w-full py-3 px-2 rounded-lg hover:bg-[#26ab4d]/10 hover:text-[#26ab4d] group active:bg-[#26ab4d]/15"
                    onClick={() => setShowProductsDropdown(!showProductsDropdown)}
                  >
                    <span className="whitespace-nowrap flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#26ab4d] opacity-60 group-hover:opacity-100 transition-opacity"></span>
                      Products
                    </span>
                    <svg className={`w-5 h-5 transition-all duration-300 text-[#26ab4d] group-hover:text-[#26ab4d] ${showProductsDropdown ? 'rotate-180 opacity-100' : 'opacity-70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showProductsDropdown && (
                    <div className="mt-5">
                      <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                          <Link
                            key={product.id}
                            to={product.id === 'pricing' ? '/pricing' : `/products/${product.id}`}
                            className="group relative p-4 rounded-2xl bg-gradient-to-br from-[#26ab4d]/8 via-[#26ab4d]/5 to-transparent hover:from-[#26ab4d]/20 hover:via-[#26ab4d]/12 hover:to-[#26ab4d]/5 transition-all duration-300 cursor-pointer border border-[#26ab4d]/20 hover:border-[#26ab4d]/50 hover:shadow-xl hover:shadow-[#26ab4d]/20 hover:-translate-y-0.5 active:translate-y-0"
                            onClick={() => {
                              setShowProductsDropdown(false);
                              setIsMenuOpen(false);
                            }}
                          >
                            {/* Glow effect on hover */}
                            <div className="absolute inset-0 rounded-2xl bg-[#26ab4d]/0 group-hover:bg-[#26ab4d]/5 transition-all duration-300 blur-xl"></div>
                            
                            {/* Product Image */}
                            <div className="relative flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                              <img src={product.image} alt={product.title} className="w-12 h-12 object-contain drop-shadow-lg" loading="lazy" decoding="async" />
                            </div>
                            
                            {/* Product Title */}
                            <h4 className="relative text-[#ffffff] text-xs font-bold text-center group-hover:text-[#26ab4d] transition-colors duration-300 mb-2 leading-tight">
                              {product.title}
                            </h4>
                            
                            {/* Sub Products as one-liner */}
                            <p className="relative text-gray-400 text-[10px] text-center leading-tight group-hover:text-gray-300 transition-colors duration-300 line-clamp-2">
                              {product.subtitle}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <Link to="/about" className="text-[#ffffff] text-base font-bold tracking-wide transition-all duration-300 py-3 px-2 rounded-lg hover:bg-[#26ab4d]/10 hover:text-[#26ab4d] border-b border-[#26ab4d]/20 pb-5 flex items-center gap-2 active:bg-[#26ab4d]/15">
                  <span className="w-1 h-1 rounded-full bg-[#26ab4d] opacity-60"></span>
                  Knowledge
                </Link>
                <div className="pt-5 space-y-4">
                  {isLoggedIn ? (
                    <>
                      <button 
                        onClick={logout}
                        className="w-full bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] text-[#ffffff] px-6 py-3.5 rounded-full text-sm font-bold hover:from-[#1e8a3f] hover:to-[#26ab4d] transition-all duration-300 shadow-lg shadow-[#26ab4d]/30 hover:shadow-xl hover:shadow-[#26ab4d]/40 hover:scale-[1.02] active:scale-[0.98]"
                      >
                        LOGOUT
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-center space-y-2">
                        <Link to="/ask-access" className="block w-full bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] text-[#ffffff] px-6 py-3.5 rounded-full text-sm font-bold hover:from-[#1e8a3f] hover:to-[#26ab4d] transition-all duration-300 shadow-lg shadow-[#26ab4d]/30 hover:shadow-xl hover:shadow-[#26ab4d]/40 hover:scale-[1.02] active:scale-[0.98]">
                          ASK FOR ACCESS
                        </Link>
                        <div className="text-gray-400 text-[10px] font-medium">
                          &lt;/&gt; API developer portal
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowLoginModal(true)}
                        className="w-full bg-black/80 backdrop-blur-sm text-[#ffffff] px-6 py-3.5 rounded-full text-sm font-bold hover:bg-black transition-all duration-300 text-center border-2 border-[#26ab4d]/40 hover:border-[#26ab4d] hover:shadow-lg hover:shadow-[#26ab4d]/20 hover:scale-[1.02] active:scale-[0.98]"
                      >
                        Login
                      </button>
                    </>
                  )}
                </div>
              </div>
            </nav>
          </div>
        </div>
        </div>
      </header>

      {/* Login Modal */}
      {showLoginModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLoginModal(false);
              setLoginError(null);
            }
          }}
        >
          <div className="bg-black backdrop-blur-sm border border-gray-600 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl h-[47.5rem] max-h-[90vh] overflow-hidden flex flex-col lg:flex-row relative">
            <button 
              onClick={() => { setShowLoginModal(false); setLoginError(null); }}
              className="absolute top-4 right-4 text-[#ffffff] hover:text-[#26ab4d] transition-colors p-1 z-10"
              aria-label="Close"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Left Side - Login Form */}
            {!isLoggedIn ? (
              <>
                <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 bg-black flex flex-col justify-center">

                  <form onSubmit={handleLogin} className="space-y-6 mx-auto max-w-md w-full">
                    <div>
                      <label className="block text-base sm:text-lg font-semibold text-[#ffffff] mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={loginData.email}
                        onChange={(e) => { setLoginData({...loginData, email: e.target.value}); setLoginError(null); }}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white rounded-full focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] placeholder-gray-400 text-sm sm:text-base"
                        placeholder="Enter your email"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-base sm:text-lg font-semibold text-[#ffffff] mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={loginData.password}
                          onChange={(e) => { setLoginData({...loginData, password: e.target.value}); setLoginError(null); }}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white rounded-full focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] placeholder-gray-400 text-sm sm:text-base"
                          placeholder="Enter your password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {loginError && (
                      <p className="text-red-500 text-sm text-center mt-2" role="alert">
                        {loginError}
                      </p>
                    )}
                  </form>

                  <div className="flex justify-center mt-8 lg:mt-10 mx-auto max-w-md w-full">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        if (loginData.email && loginData.password) {
                          handleLogin(e as any);
                        }
                      }}
                      className="px-5 lg:px-8 py-2 lg:py-3 xl:py-4 rounded-full bg-[#26ab4d] hover:bg-[#1e8a3f] text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-bold tracking-wide transition-all duration-300 whitespace-nowrap min-w-[120px] lg:min-w-[170px]"
                    >
                      Login
                    </button>
                  </div>

                  <div className="mt-8 lg:mt-10 text-center">
                    <p className="text-sm text-[#ffffff]">
                      Don't have an account? 
                      <Link to="/ask-access" onClick={() => setShowLoginModal(false)} className="text-[#26ab4d] hover:underline ml-1">
                        Ask for access
                      </Link>
                    </p>
                  </div>
                </div>
                
                {/* Right Side - Developer Portal Image */}
                <div className="hidden lg:flex w-full lg:w-1/2 items-center justify-center p-0 relative bg-black">
                  <div className="w-full h-full flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#26ab4d]/5 to-transparent"></div>
                    <img 
                      src="/login-page-icons/developer%20portal.png" 
                      alt="API Developer Portal Graphic" 
                      className="w-full h-full max-w-none object-contain opacity-95 relative z-10"
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      width="800"
                      height="600"
                      style={{ 
                        transform: 'scale(0.9)',
                        objectPosition: 'center center',
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.error('Failed to load login page image:', e);
                        // Fallback: try with space encoded differently
                        const target = e.target as HTMLImageElement;
                        if (target.src.includes('%20')) {
                          target.src = '/login-page-icons/developer portal.png';
                        }
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full p-8 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-[#ffffff] mb-4">Successfully Logged In</h2>
                  <button 
                    onClick={() => {
                      setShowLoginModal(false);
                      logout();
                    }}
                    className="bg-[#26ab4d] text-[#ffffff] px-6 py-3 rounded-full font-semibold hover:bg-[#1e8a3f] transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;